/**
 * Service Result Type
 * Standardized return type for all service operations
 * 
 * @module types/api/result
 */

import { AppError } from '../../lib/errors';

/**
 * Success result
 */
export interface SuccessResult<T> {
    success: true;
    data: T;
}

/**
 * Error result
 */
export interface ErrorResult {
    success: false;
    error: AppError;
}

/**
 * Service result - either success with data or error
 */
export type ServiceResult<T> = SuccessResult<T> | ErrorResult;

/**
 * Create a success result
 */
export function ok<T>(data: T): SuccessResult<T> {
    return { success: true, data };
}

/**
 * Create an error result
 */
export function err(error: AppError): ErrorResult {
    return { success: false, error };
}

/**
 * Check if result is success
 */
export function isOk<T>(result: ServiceResult<T>): result is SuccessResult<T> {
    return result.success === true;
}

/**
 * Check if result is error
 */
export function isErr<T>(result: ServiceResult<T>): result is ErrorResult {
    return result.success === false;
}

/**
 * Unwrap result or throw
 */
export function unwrap<T>(result: ServiceResult<T>): T {
    if (isOk(result)) {
        return result.data;
    }
    throw result.error;
}

/**
 * Unwrap result or return default
 */
export function unwrapOr<T>(result: ServiceResult<T>, defaultValue: T): T {
    if (isOk(result)) {
        return result.data;
    }
    return defaultValue;
}
